import React, { useState, useEffect } from "react";
import { Badge, Dropdown, List, Button, Typography } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { Container } from './styles.jsx';
import Api, { exceptionNotificationAPI } from "../../services/api";
import moment from "moment";

const { Text } = Typography;

const NotificationMenu = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    const timer = setInterval(async () => {
      await fetchNewNotifications();
    }, 180000); // 3 minutos
    return () => clearInterval(timer);
  }, [data]);

  const getData = async () => {
    const user = JSON.parse(localStorage.getItem('@Thrive:user'));
    
    try {
      const result = await Api.get(`Notification/GetByUserId?cultureID=${user?.selectedCultureID ?? ''}`);
      if (result && result.data) {
        const list = [];
        let countNotRead = 0;
        
        result.data.response.forEach((not) => {
          const notIsRead = not.usersIDs.find(x => x.userID == user.id);
          
          const notification = {
            notificationID: not.notificationID,
            message: not.description,
            receivedTime: moment(not.created).add(moment().utcOffset(), "m").fromNow(),
            isRead: notIsRead.isRead
          };
          
          if (!notification.isRead) {
            countNotRead++;
          }
          
          list.push(notification);
        });
        
        setData(list);
        setCount(countNotRead);
        setLoading(false);
      }
    } catch (error) {
      exceptionNotificationAPI(error);
    }
  };

  const fetchNewNotifications = async () => {
    const user = JSON.parse(localStorage.getItem('@Thrive:user'));
    const notificationsIDsList = data.map(notify => parseInt(notify.notificationID));

    try {
      const result = await Api.post(`Notification/GetNewNotificationsByUserId?cultureID=${user?.selectedCultureID ?? ''}`, notificationsIDsList);
      if (result && result.data && result.data.response.length > 0) {
        const newList = [];
        let countNotRead = count;
        
        result.data.response.forEach((not) => {
          const notIsRead = not.usersIDs.find(x => x.userID == user.id);
          
          const notification = {
            notificationID: not.notificationID,
            message: not.description,
            receivedTime: moment(not.created).add(moment().utcOffset(), "m").fromNow(),
            isRead: notIsRead != null ? notIsRead.isRead : false
          };
          
          if (!notification.isRead) {
            countNotRead++;
          }
          
          newList.push(notification);
        });
        
        setData([...data, ...newList]);
        setCount(countNotRead);
      }
    } catch (error) {
      exceptionNotificationAPI(error);
    }
  };

  const onReadAll = async () => {
    try {
      await Promise.all(
        data.map(async (notify) => {
          if (!notify.isRead) {
            await Api.post(`Notification/UpdateIRead?id=${notify.notificationID}`);
            notify.isRead = true;
          }
        })
      );
      
      setData([...data]);
      setCount(0);
    } catch (error) {
      exceptionNotificationAPI(error);
    }
  };

  const markAsRead = async (notify) => {
    if (!notify.isRead) {
      try {
        await Api.post(`Notification/UpdateIRead?id=${notify.notificationID}`);
        
        const updatedData = data.map(item => 
          item.notificationID === notify.notificationID 
            ? { ...item, isRead: true } 
            : item
        );
        
        setData(updatedData);
        setCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        exceptionNotificationAPI(error);
      }
    }
  };

  const menuItems = () => (
    <div style={{ 
      width: 400, 
      maxHeight: 500, 
      backgroundColor: '#fff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      borderRadius: 4
    }}>
      {/* Header */}
      <div style={{ 
        padding: '12px 16px', 
        backgroundColor: '#102A63',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4
      }}>
        <Text strong style={{ color: '#fff', fontSize: 16 }}>
          Notifications
        </Text>
        <Button 
          type="link" 
          onClick={onReadAll}
          style={{ 
            color: '#fff', 
            textDecoration: 'underline',
            padding: 0 
          }}
        >
          Mark all as read
        </Button>
      </div>

      {/* Lista de notificações */}
      <div style={{ maxHeight: 450, overflowY: 'auto' }}>
        {data.length === 0 ? (
          <div style={{ 
            padding: 40, 
            textAlign: 'center',
            color: '#999'
          }}>
            No notifications
          </div>
        ) : (
          <List
            dataSource={data}
            renderItem={(item) => (
              <List.Item
                onClick={() => markAsRead(item)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: !item.isRead ? '#ebedf0' : '#fff',
                  padding: '12px 16px',
                  borderBottom: '1px solid #f0f0f0',
                  transition: 'background-color 0.3s'
                }}
                onMouseEnter={(e) => {
                  if (item.isRead) {
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = !item.isRead ? '#ebedf0' : '#fff';
                }}
              >
                <List.Item.Meta
                  description={
                    <div>
                      <div style={{ 
                        color: '#000',
                        marginBottom: 4,
                        fontSize: 12,
                        fontWeight: !item.isRead ? 600 : 400
                      }}>
                        {item.message}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.receivedTime}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );

  return (
    <Container>
      <Dropdown
        dropdownRender={menuItems}
        trigger={['click']}
        open={open}
        onOpenChange={setOpen}
        placement="bottomRight"
      >
        <Badge 
          count={count} 
          offset={[-5, 5]}
          style={{ backgroundColor: '#ff0000' }}
        >
          <BellOutlined 
            style={{ 
              fontSize: 24, 
              cursor: 'pointer',
              color: '#102A63'
            }} 
          />
        </Badge>
      </Dropdown>
    </Container>
  );
};

export default NotificationMenu;

